import { ProjectMappingMethod } from "@gx/ts-contracts/services/project/resources/project_configuration/project_configuration_pb";

import {
  CreateProjectFormAction,
  CreateProjectFormActionType,
  SetAutoIntrospectPromptAction,
  SetCreateProjectRequestAction,
  SetCreateProjectSourcesRequestAction,
  SetIsLoadingAction,
  SetIsUpdateProjectSucceededAction,
  SetProjectSyncCronExpressionAction,
  SetSearchAction,
  SetSelectedMappingMethodAction,
  SetSelectedSourceTypesAction,
  SetSelectedStatusesAction,
  SetShouldShowErrorsAction,
  SetStepAction,
  SetUpdateProjectRequestAction,
} from "@/pages/projects/forms/create/actions";
import { CREATE_PROJECT_FORM_STEPS } from "@/pages/projects/forms/create/constants";
import { CreateProjectFormState } from "@/pages/projects/forms/create/types";

function setStep(state: CreateProjectFormState, action: SetStepAction): CreateProjectFormState {
  const currentStepIndex = CREATE_PROJECT_FORM_STEPS.indexOf(action.payload);
  return {
    ...state,
    step: action.payload,
    navigation: {
      ...state.navigation,
      currentStepIndex,
      isFirstStep: currentStepIndex === 0,
      isLastStep: currentStepIndex === CREATE_PROJECT_FORM_STEPS.length - 1,
      canGoBack: currentStepIndex > 0,
      canGoForward: currentStepIndex < CREATE_PROJECT_FORM_STEPS.length - 1,
    },
  };
}

function setSearch(state: CreateProjectFormState, action: SetSearchAction): CreateProjectFormState {
  return {
    ...state,
    search: action.payload,
  };
}

function setCreateProjectRequest(
  state: CreateProjectFormState,
  action: SetCreateProjectRequestAction,
): CreateProjectFormState {
  return {
    ...state,
    createProjectRequest: action.payload,
  };
}

function setUpdateProjectRequest(
  state: CreateProjectFormState,
  action: SetUpdateProjectRequestAction,
): CreateProjectFormState {
  return {
    ...state,
    updateProjectRequest: action.payload,
  };
}

function setIsLoading(
  state: CreateProjectFormState,
  action: SetIsLoadingAction,
): CreateProjectFormState {
  return {
    ...state,
    isLoading: action.payload,
  };
}

function setIsUpdateProjectSucceeded(
  state: CreateProjectFormState,
  action: SetIsUpdateProjectSucceededAction,
): CreateProjectFormState {
  return {
    ...state,
    isUpdateProjectSucceeded: action.payload,
  };
}

function setCreateProjectSourcesRequest(
  state: CreateProjectFormState,
  action: SetCreateProjectSourcesRequestAction,
): CreateProjectFormState {
  return {
    ...state,
    createProjectSourcesRequest: action.payload,
  };
}

function setShouldShowErrors(
  state: CreateProjectFormState,
  action: SetShouldShowErrorsAction,
): CreateProjectFormState {
  return {
    ...state,
    shouldShowErrors: action.payload,
  };
}

function setSelectedSourceTypes(
  state: CreateProjectFormState,
  action: SetSelectedSourceTypesAction,
): CreateProjectFormState {
  return {
    ...state,
    selectedSourceTypes: action.payload,
  };
}

function setSelectedStatuses(
  state: CreateProjectFormState,
  action: SetSelectedStatusesAction,
): CreateProjectFormState {
  return {
    ...state,
    selectedStatuses: action.payload,
  };
}

function setSelectedMappingMethod(
  state: CreateProjectFormState,
  action: SetSelectedMappingMethodAction,
): CreateProjectFormState {
  return {
    ...state,
    selectedMappingMethod: action.payload,
    // Clear the prompt when switching away from AUTO
    autoIntrospectPrompt:
      action.payload === ProjectMappingMethod.AUTO ? state.autoIntrospectPrompt : "",
  };
}

function setAutoIntrospectPrompt(
  state: CreateProjectFormState,
  action: SetAutoIntrospectPromptAction,
): CreateProjectFormState {
  return {
    ...state,
    autoIntrospectPrompt: action.payload,
  };
}

function setProjectSyncCronExpression(
  state: CreateProjectFormState,
  action: SetProjectSyncCronExpressionAction,
): CreateProjectFormState {
  return {
    ...state,
    projectSyncCronExpression: action.payload,
  };
}

const createProjectFormReducer = (
  state: CreateProjectFormState,
  action: CreateProjectFormAction,
): CreateProjectFormState => {
  switch (action.type) {
    case CreateProjectFormActionType.SET_STEP:
      return setStep(state, action);
    case CreateProjectFormActionType.SET_SEARCH:
      return setSearch(state, action);
    case CreateProjectFormActionType.SET_CREATE_PROJECT_REQUEST:
      return setCreateProjectRequest(state, action);
    case CreateProjectFormActionType.SET_UPDATE_PROJECT_REQUEST:
      return setUpdateProjectRequest(state, action);
    case CreateProjectFormActionType.SET_IS_LOADING:
      return setIsLoading(state, action);
    case CreateProjectFormActionType.SET_IS_UPDATE_PROJECT_SUCCEEDED:
      return setIsUpdateProjectSucceeded(state, action);
    case CreateProjectFormActionType.SET_CREATE_PROJECT_SOURCES_REQUEST:
      return setCreateProjectSourcesRequest(state, action);
    case CreateProjectFormActionType.SET_SHOULD_SHOW_ERRORS:
      return setShouldShowErrors(state, action);
    case CreateProjectFormActionType.SET_SELECTED_SOURCE_TYPES:
      return setSelectedSourceTypes(state, action);
    case CreateProjectFormActionType.SET_SELECTED_STATUSES:
      return setSelectedStatuses(state, action);
    case CreateProjectFormActionType.SET_SELECTED_MAPPING_METHOD:
      return setSelectedMappingMethod(state, action);
    case CreateProjectFormActionType.SET_AUTO_INTROSPECT_PROMPT:
      return setAutoIntrospectPrompt(state, action);
    case CreateProjectFormActionType.SET_PROJECT_SYNC_CRON_EXPRESSION:
      return setProjectSyncCronExpression(state, action);
  }
};

export default createProjectFormReducer;
